import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, UserMinus, CheckCircle2, AlertTriangle,
  Search, ShieldOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

// iter131 SEC-001 closer — admin data-footprint check + soft
// deactivate. Meant primarily for retiring the old seeded admin
// (admin@braintrades.com) once a real-inbox admin has taken over.
// The page enforces a "look before you leap" flow: the deactivate
// button is only clickable after a fresh footprint check has been
// run for the current email. Route: /admin/deactivate-user
// (admin-only via backend 403).

export default function AdminDeactivateUser() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [footprint, setFootprint] = useState(null);  // API response
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);     // for the check
  const [busy, setBusy] = useState(false);           // for the deactivate
  const [result, setResult] = useState(null);        // after deactivation
  const [error, setError] = useState(null);

  const canCheck = email.trim().length > 3 && !loading && !busy;
  const canDeactivate = footprint && confirmChecked && !busy && !result;

  const check = async () => {
    if (!canCheck) return;
    setLoading(true);
    setFootprint(null);
    setError(null);
    setResult(null);
    setConfirmChecked(false);
    try {
      const { data } = await api.get(
        `/admin/users/audit-footprint?email=${encodeURIComponent(email.trim())}`
      );
      setFootprint(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      if (status === 403) {
        setError({ headline: "Admins only", body: "This page is admin-only." });
      } else if (status === 404) {
        setError({ headline: "User not found", body: detail });
      } else if (status === 400) {
        setError({ headline: "Invalid email", body: detail });
      } else {
        setError({ headline: "Couldn't check footprint", body: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const deactivate = async () => {
    if (!canDeactivate) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/users/deactivate", {
        email: email.trim(),
        reason: reason.trim(),
      });
      setResult(data);
      toast.success(`${data.email} deactivated`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      setError({
        headline: status === 403 ? "Admins only" : "Couldn't deactivate",
        body: detail,
      });
    } finally {
      setBusy(false);
    }
  };

  const dismissResult = () => {
    if (result?.self_deactivated) {
      toast.info("You deactivated your own account — please sign in again");
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }
    setResult(null);
    setFootprint(null);
    setEmail("");
    setReason("");
    setConfirmChecked(false);
  };

  const dataRowsList = footprint
    ? Object.entries(footprint.data_counts).filter(([_, n]) => n > 0)
    : [];

  return (
    <div data-testid="admin-deactivate-user-page" className="space-y-6 max-w-2xl">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
        data-testid="admin-deactivate-user-back"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </Link>

      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <UserMinus className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Admin · sensitive</p>
        </div>
        <h1 className="font-heading text-3xl">Deactivate User</h1>
        <p className="text-sm text-[var(--bt-text-muted)]">
          Soft-deactivate an account: remove admin role, downgrade to
          free tier, and invalidate all sessions. The user row is
          preserved so audit-log references stay intact (nothing is
          deleted). Data-footprint check is required before the
          deactivate button unlocks.
        </p>
      </header>

      {/* Step 1 — email + check */}
      {!result && (
        <section
          data-testid="admin-deactivate-user-form"
          className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
        >
          <div>
            <label className="bt-label block mb-1.5">User email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFootprint(null); setConfirmChecked(false); }}
                onKeyDown={(e) => { if (e.key === "Enter") check(); }}
                placeholder="admin@braintrades.com"
                className="flex-1 px-3 py-2 border border-[var(--bt-border)] rounded-sm bg-[var(--bt-bg)] text-sm focus:outline-none focus:border-[var(--bt-accent)]"
                data-testid="admin-deactivate-user-email"
                autoFocus
              />
              <Button
                onClick={check}
                disabled={!canCheck}
                variant="outline"
                data-testid="admin-deactivate-user-check"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
                Check
              </Button>
            </div>
          </div>

          {footprint && (
            <div
              data-testid="admin-deactivate-user-footprint"
              className="border border-[var(--bt-border)] bg-[var(--bt-bg)] rounded-sm p-4 space-y-3"
            >
              <div>
                <p className="bt-label mb-1">Current state</p>
                <dl className="text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)]">Role</dt><dd className="font-medium">{footprint.user.role}</dd></div>
                  <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)]">Tier</dt><dd className="font-medium">{footprint.user.subscription_tier}</dd></div>
                  <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)]">Created</dt><dd>{new Date(footprint.user.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</dd></div>
                  <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)]">Sessions bumped</dt><dd>{footprint.user.token_version}×</dd></div>
                </dl>
              </div>

              <div>
                <p className="bt-label mb-1">Data footprint</p>
                {footprint.is_empty ? (
                  <p className="text-sm flex items-center gap-1.5" style={{ color: "var(--bt-win)" }}>
                    <CheckCircle2 className="w-4 h-4" />
                    No user data — safe to deactivate.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-[var(--bt-text-muted)] mb-1">
                      This account has <strong className="text-[var(--bt-text)]">{footprint.totals.data_rows}</strong> data rows. Deactivation does NOT delete them.
                    </p>
                    <ul className="text-sm space-y-0.5 pl-4 list-disc marker:text-[var(--bt-text-muted)]">
                      {dataRowsList.map(([label, n]) => (
                        <li key={label}>
                          <span className="font-mono text-xs text-[var(--bt-text-muted)]">{label}</span>:{" "}
                          <strong>{n.toLocaleString()}</strong>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div>
                <p className="bt-label mb-1">Audit references (preserved)</p>
                <p className="text-sm text-[var(--bt-text-muted)]">
                  {footprint.totals.audit_refs} row{footprint.totals.audit_refs === 1 ? "" : "s"} across{" "}
                  <span className="font-mono text-xs">set_tier_audit</span>,{" "}
                  <span className="font-mono text-xs">admin_rotation_audit</span>,{" "}
                  <span className="font-mono text-xs">deactivation_audit</span>{" "}
                  reference this user. Kept intact.
                </p>
              </div>
            </div>
          )}

          {footprint && (
            <>
              <div>
                <label className="bt-label block mb-1.5">Reason (optional)</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. seed admin retired, replaced by admin@braintrades.ai"
                  maxLength={500}
                  className="w-full px-3 py-2 border border-[var(--bt-border)] rounded-sm bg-[var(--bt-bg)] text-sm focus:outline-none focus:border-[var(--bt-accent)]"
                  data-testid="admin-deactivate-user-reason"
                />
              </div>

              <label
                className="flex items-start gap-2 cursor-pointer select-none"
                data-testid="admin-deactivate-user-confirm-label"
              >
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-0.5"
                  data-testid="admin-deactivate-user-confirm"
                />
                <span className="text-sm text-[var(--bt-text-muted)]">
                  I confirm this account should be soft-deactivated:{" "}
                  <strong className="text-[var(--bt-text)]">role → user</strong>,{" "}
                  <strong className="text-[var(--bt-text)]">tier → free</strong>, all sessions signed out.
                  Data rows and audit refs are preserved.
                </span>
              </label>

              <div className="flex justify-end">
                <Button
                  onClick={deactivate}
                  disabled={!canDeactivate}
                  data-testid="admin-deactivate-user-submit"
                  className="bg-[var(--bt-loss)] hover:bg-[var(--bt-loss)]/90 text-white"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Deactivating…</>
                  ) : (
                    <><ShieldOff className="w-4 h-4 mr-1.5" /> Deactivate</>
                  )}
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      {result && (
        <section
          data-testid="admin-deactivate-user-result"
          className="border border-[var(--bt-win)]/40 bg-[var(--bt-win)]/5 rounded-sm p-5 space-y-2"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
            <p className="font-medium">Deactivated</p>
          </div>
          <dl className="text-sm space-y-1">
            <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)] w-24">Email</dt><dd data-testid="admin-deactivate-user-result-email">{result.email}</dd></div>
            <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)] w-24">Role</dt><dd data-testid="admin-deactivate-user-result-role"><span className="text-[var(--bt-text-muted)]">{result.from_role}</span> → <span className="font-medium">{result.to_role}</span></dd></div>
            <div className="flex gap-2"><dt className="text-[var(--bt-text-muted)] w-24">Tier</dt><dd data-testid="admin-deactivate-user-result-tier"><span className="text-[var(--bt-text-muted)]">{result.from_tier}</span> → <span className="font-medium">{result.to_tier}</span></dd></div>
          </dl>
          <div className="pt-2">
            <Button
              onClick={dismissResult}
              variant="outline"
              data-testid="admin-deactivate-user-dismiss"
            >
              Done
            </Button>
          </div>
        </section>
      )}

      {error && (
        <section
          data-testid="admin-deactivate-user-error"
          className="border border-[var(--bt-loss)]/40 bg-[var(--bt-loss)]/5 rounded-sm p-5 space-y-1"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
            <p className="font-medium">{error.headline}</p>
          </div>
          <p className="text-sm text-[var(--bt-text-muted)]">{error.body}</p>
        </section>
      )}
    </div>
  );
}
